import { LockAccessCodeStatus, LockAccessCodeType } from '@core/enums';
import {
  DeviceStatus,
  type LockAccessCode,
  type LockAccessHistory,
  type LockActionResponse,
  type LockBattery,
  type LockDevice,
} from '@interfaces/lock.interface';

import { LOCK_ACCESS_METHODS, LOCK_ACTIVITIES, LOCK_DEVICE_NAMES, LOCK_TYPES } from '../pools';
import { daysFromNow, isoNow, pick, pickAt, randomBool, randomInt, uuid } from '../utils';

export function buildLockDevice(index: number, overrides?: Partial<LockDevice>): LockDevice {
  return {
    deviceId: uuid(),
    name: pickAt(LOCK_DEVICE_NAMES, index),
    batteryLevel: randomInt(50, 100),
    status: randomBool(0.9) ? DeviceStatus.ONLINE : DeviceStatus.OFFLINE,
    type: pick(LOCK_TYPES),
    isMainDevice: index === 0,
    createdAt: isoNow(-randomInt(30, 365)),
    pairedAt: isoNow(-randomInt(1, 30)),
    updatedAt: isoNow(-randomInt(0, 7)),
    rssi: randomInt(-90, -30),
    unlockDirection: 'right',
    ...overrides,
  };
}

export function buildLockDevices(count: number): LockDevice[] {
  return Array.from({ length: count }, (_, i) => buildLockDevice(i));
}

export function buildAccessCode(
  index: number,
  overrides?: Partial<LockAccessCode>
): LockAccessCode {
  const isExpired = randomBool(0.2);
  return {
    codeName: `Guest Code ${index + 1}`,
    codeType: pick([LockAccessCodeType.AUTOMATIC, LockAccessCodeType.MANUAL]),
    startDate: daysFromNow(isExpired ? -20 : -2),
    endDate: daysFromNow(isExpired ? -5 : 10),
    passcode: String(randomInt(1000, 9999)),
    status: isExpired ? LockAccessCodeStatus.EXPIRED : LockAccessCodeStatus.ACTIVE,
    ...overrides,
  };
}

export function buildAccessCodes(count: number): LockAccessCode[] {
  return Array.from({ length: count }, (_, i) => buildAccessCode(i));
}

export function buildAccessHistory(index: number): LockAccessHistory {
  const success = randomBool(0.85);
  return {
    access: String(randomInt(1000, 9999)),
    accessMethod: pick(LOCK_ACCESS_METHODS),
    status: success ? 'SUCCESS' : 'FAILED',
    statusCode: success ? 200 : 401,
    activity: pick(LOCK_ACTIVITIES),
    date: daysFromNow(-index),
    time: `${String(randomInt(6, 23)).padStart(2, '0')}:${String(randomInt(0, 59)).padStart(2, '0')}`,
  };
}

export function buildAccessHistoryList(count: number): LockAccessHistory[] {
  return Array.from({ length: count }, (_, i) => buildAccessHistory(i));
}

export function buildLockBattery(device: LockDevice): LockBattery {
  return {
    deviceId: device.deviceId,
    batteryLevel: device.batteryLevel ?? 85,
    type: device.type ?? 'smart',
    pairedAt: device.pairedAt ?? isoNow(-7),
  };
}

export function buildLockActionResponse(device: LockDevice): LockActionResponse {
  return {
    success: true,
    data: {
      batteryLevel: device.batteryLevel ?? null,
      createdAt: device.createdAt ?? null,
      deviceId: device.deviceId,
      name: device.name ?? null,
      pairedAt: device.pairedAt ?? null,
      rssi: device.rssi ?? null,
      type: device.type ?? null,
      unlockDirection: device.unlockDirection ?? null,
      updatedAt: device.updatedAt ?? null,
    },
    error: null,
  };
}
